import { useState } from 'react'
import { useSelector } from 'react-redux'
import Chevron from 'assets/icons/Chevron'
import useQueryPagination from 'hooks/useQueryPagination'
import { clearAccommodationInfo, getAccommodationStatistics, getCamps } from 'features/accommodation/accommodationSlice'
import { RootState } from 'store'
import { IGetAllCampsSingleResponse } from 'types/api/accommodationApi.interface'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { tokens } from 'styling/tokens'
import { BarTrack, BarFill } from 'pages/HrDashboard/hrLayout.styled'
import { BedIcon } from '../icons/BedIcon'
import { OccupiedBedIcon } from '../icons/OccupiedBedIcon'
import { BlockedIcon } from '../icons/BlockedIcon'
import { DirtyIcon } from '../icons/DirtyIcon'
import {
  StatBand,
  SectionLabel,
  SectionCount,
  CampList,
  CampCard,
  CampHeader,
  CampIdentity,
  CampName,
  CampMeta,
  CampKpis,
  CampKpi,
  CampKpiValue,
  CampKpiLabel,
  ChevronBox,
  CampCollapse,
  CampCollapseInner,
  BlockList,
  BlockRow,
  BlockIdentity,
  BlockName,
  BlockMeta,
  BlockBar,
  BlockBarValue,
  CountGroup,
  CountChip,
  CountDot,
  CountValue,
  CountLabel,
  BlockEmpty,
} from './AccommodationDashboard.styled'

const C = {
  accent: tokens.accent.base,
  available: tokens.status.success.fg,
  occupied: tokens.status.info.fg,
  dirty: tokens.status.warning.fg,
  blocked: tokens.status.danger.fg,
}

interface BlockStat {
  name: string
  rooms: number
  beds: number
  available: number
  occupied: number
  dirty: number
  blocked: number
  occupancy: number
}

const computeCamp = (camp: IGetAllCampsSingleResponse) => {
  let rooms = 0
  let beds = 0
  let available = 0
  let occupied = 0
  let dirty = 0
  let blocked = 0

  const blocks: BlockStat[] = (camp.blocks || []).map((block) => {
    let bRooms = 0
    let bBeds = 0
    let bAvailable = 0
    let bOccupied = 0
    let bDirty = 0
    let bBlocked = 0

    ;(block.rooms || []).forEach((room) => {
      bRooms += 1
      rooms += 1
      ;(room.beds || []).forEach((bed) => {
        bBeds += 1
        beds += 1
        switch (bed.state) {
          case 1:
            bAvailable += 1
            available += 1
            break
          case 2:
            bOccupied += 1
            occupied += 1
            break
          case 3:
            bDirty += 1
            dirty += 1
            break
          case 4:
            bBlocked += 1
            blocked += 1
            break
        }
      })
    })

    return {
      name: block.name,
      rooms: bRooms,
      beds: bBeds,
      available: bAvailable,
      occupied: bOccupied,
      dirty: bDirty,
      blocked: bBlocked,
      occupancy: bBeds ? Math.round((bOccupied / bBeds) * 100) : 0,
    }
  })

  return {
    rooms,
    beds,
    available,
    occupied,
    dirty,
    blocked,
    occupancy: beds ? Math.round((occupied / beds) * 100) : 0,
    blocks,
  }
}

const Count = ({ color, value, label }: { color: string; value: number; label: string }) => {
  const dim = value === 0
  return (
    <CountChip>
      <CountDot $color={dim ? tokens.border.strong : color} />
      <CountValue $dim={dim}>{value}</CountValue>
      <CountLabel>{label}</CountLabel>
    </CountChip>
  )
}

const CampSection = ({
  camp,
  active,
  onToggle,
}: {
  camp: IGetAllCampsSingleResponse
  active: boolean
  onToggle: () => void
}) => {
  const stats = computeCamp(camp)

  return (
    <CampCard $active={active}>
      <CampHeader onClick={onToggle}>
        <CampIdentity>
          <CampName>{camp.name}</CampName>
          <CampMeta>
            {camp.blocks?.length || 0} blocks · {stats.rooms} rooms · {stats.beds} beds
          </CampMeta>
        </CampIdentity>
        <CampKpis>
          <CampKpi>
            <CampKpiValue $color={C.occupied}>{stats.occupancy}%</CampKpiValue>
            <CampKpiLabel>Occupancy</CampKpiLabel>
          </CampKpi>
          <CampKpi>
            <CampKpiValue $color={C.available}>{stats.available}</CampKpiValue>
            <CampKpiLabel>Available</CampKpiLabel>
          </CampKpi>
          <CampKpi>
            <CampKpiValue $color={stats.dirty ? C.dirty : tokens.text.secondary}>{stats.dirty}</CampKpiValue>
            <CampKpiLabel>Cleaning</CampKpiLabel>
          </CampKpi>
          <ChevronBox>
            <Chevron open={active} />
          </ChevronBox>
        </CampKpis>
      </CampHeader>

      <CampCollapse $open={active}>
        <CampCollapseInner>
          <BlockList>
            {stats.blocks.length ? (
              stats.blocks.map((b, i) => (
                <BlockRow key={i}>
                  <BlockIdentity>
                    <BlockName>{b.name || `Block ${i + 1}`}</BlockName>
                    <BlockMeta>
                      {b.rooms} {b.rooms === 1 ? 'room' : 'rooms'} · {b.beds} {b.beds === 1 ? 'bed' : 'beds'}
                    </BlockMeta>
                  </BlockIdentity>
                  <BlockBar>
                    <BarTrack>
                      <BarFill $pct={b.occupancy} $color={C.occupied} />
                    </BarTrack>
                    <BlockBarValue>{b.occupancy}%</BlockBarValue>
                  </BlockBar>
                  <CountGroup>
                    <Count color={C.available} value={b.available} label='Available' />
                    <Count color={C.occupied} value={b.occupied} label='Occupied' />
                    <Count color={C.dirty} value={b.dirty} label='Dirty' />
                    <Count color={C.blocked} value={b.blocked} label='Blocked' />
                  </CountGroup>
                </BlockRow>
              ))
            ) : (
              <BlockEmpty>No blocks configured for this camp</BlockEmpty>
            )}
          </BlockList>
        </CampCollapseInner>
      </CampCollapse>
    </CampCard>
  )
}

export const AccomodationDashboard = () => {
  const [params] = useState({ page: 1, limit: 99 })
  const [openCampId, setOpenCampId] = useState<number | null>(null)
  const { camps, statistics } = useSelector((state: RootState) => state.accomodation)
  useQueryPagination({
    fetchFunctions: [getCamps, getAccommodationStatistics],
    params,
    cleanUpFunctions: [clearAccommodationInfo],
  })

  const occupancyPct = statistics?.totalCapacity
    ? Math.round(((statistics.occupied || 0) / statistics.totalCapacity) * 100)
    : 0
  const campsData = camps?.data || []

  return (
    <>
      <StatBand>
        <MetricCard
          layout='inline'
          label='Total capacity'
          value={String(statistics?.totalCapacity || 0)}
          icon={<BedIcon />}
          accentColor={C.accent}
        />
        <MetricCard
          layout='inline'
          label='Available'
          value={String(statistics?.available || 0)}
          icon={<BedIcon color={C.available} />}
          accentColor={C.available}
        />
        <MetricCard
          layout='inline'
          label='Occupied'
          value={String(statistics?.occupied || 0)}
          icon={<OccupiedBedIcon />}
          accentColor={C.occupied}
          trailing={<StatusPill status='info'>{occupancyPct}%</StatusPill>}
        />
        <MetricCard
          layout='inline'
          label='Dirty'
          value={String(statistics?.dirty || 0)}
          icon={<DirtyIcon />}
          accentColor={C.dirty}
        />
        <MetricCard
          layout='inline'
          label='Blocked'
          value={String(statistics?.blocked || 0)}
          icon={<BlockedIcon />}
          accentColor={C.blocked}
        />
      </StatBand>

      <SectionLabel>
        Camps <SectionCount>{campsData.length}</SectionCount>
      </SectionLabel>
      <CampList>
        {campsData.map((c) => (
          <CampSection
            key={c.id}
            camp={c}
            active={openCampId === c.id}
            onToggle={() => setOpenCampId((prev) => (prev === c.id ? null : c.id))}
          />
        ))}
      </CampList>
    </>
  )
}
